import React, { useMemo, useState, useEffect } from 'react';
import { isNativePlatform } from '../../../utils/platform';
import { CONFIGURACION_WEB_ACTUAL, ConfiguracionTiqueteWeb, normalizarConfiguracionWeb, aplicarPresetEspacioWeb, reconocerPresetEspacioWeb } from '../../../services/ticketWebConfig';
import { leerVisualConfig } from '../../../services/ticketService';
import { fetchAPI } from '../../../services/apiClient';
import { renderTicketPreviewHTML, TicketConfig, TicketConfigApiResponse, TicketPayload } from '../../../services/ticketService';

export const LocalConfigTicketPage: React.FC = () => {
  const esWeb = !isNativePlatform();
  const [configCargada, setConfigCargada] = useState(false);
  const [configWeb, setConfigWeb] = useState<ConfiguracionTiqueteWeb | null>(null);
  const [esPropio, setEsPropio] = useState(true);
  const [ejemplo, setEjemplo] = useState<'simple' | 'extenso'>('simple');
  const endpoint = esWeb ? '/operaciones/local/ticket-config-web' : '/operaciones/local/ticket-config';
  const [bancaNombre, setBancaNombre] = useState('TiemPos');
  const [bancaLogo, setBancaLogo] = useState<string | null>(null);
  const [notasTicketDefault, setNotasTicketDefault] = useState('');
  const [leyendaTicket, setLeyendaTicket] = useState('');
  const [tipoImpresora, setTipoImpresora] = useState<'80mm' | '58mm'>('80mm');
  const [telefono, setTelefono] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    cargarConfig();
  }, []);

  const cargarConfig = async () => {
    try {
      const data = await fetchAPI<TicketConfigApiResponse>(
        endpoint
      );
      if (esWeb) {
        setConfigWeb(normalizarConfiguracionWeb(data.configuracion_tiquete_web));
        setEsPropio(Boolean(data.es_propio));
      }
      setBancaNombre(data.banca_nombre || 'TiemPos');
      setBancaLogo(data.banca_logo || null);
      setNotasTicketDefault(data.ticket_notas_default || '');
      setLeyendaTicket(data.leyenda_ticket || '');
      setTipoImpresora(data.tipo_impresora === '58mm' ? '58mm' : '80mm');
      setTelefono(data.telefono || '');
      setConfigCargada(true);
    } catch (error: any) {
      console.error('Error cargando configuración:', error);
      if (esWeb) setMensaje('Error: no se pudo cargar la configuración WEB. Verifique que el backend y la migración estén preparados.');
    }
  };

  const guardarConfig = async () => {
    setLoading(true);
    setMensaje('');
    try {
      await fetchAPI(endpoint, {
        method: 'PUT',
        body: JSON.stringify({
          ...(esWeb ? { configuracion_tiquete_web: configWeb } : {}),
          ticket_notas_default: notasTicketDefault.trim() || null,
          tipo_impresora: tipoImpresora,
          leyenda_ticket: leyendaTicket.trim() || null,
          telefono: telefono.trim() || null,
        }),
      });
      setMensaje('Configuración guardada correctamente');
      setTimeout(() => setMensaje(''), 3000);
    } catch (error: any) {
      setMensaje(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const previewPayload = useMemo<TicketPayload>(() => {
    const config: TicketConfig = {
      nombre: bancaNombre.trim() || 'TiemPos',
      notas: notasTicketDefault.trim(),
      ancho: tipoImpresora === '58mm' ? 58 : 80,
      logoUrl: bancaLogo,
      logoDataUrl: null,
      mostrarLogo: Boolean(bancaLogo),
      fontWeight: esWeb ? (leerVisualConfig().fontWeight || 'bold') : 'normal',
      fontSize: esWeb ? (leerVisualConfig().fontSize || 'normal') : 'normal',
      ...(esWeb ? { configuracionWeb: configWeb } : {}),
      leyendaLocal: leyendaTicket.trim() || undefined,
      tipoImpresora,
    };

    return {
      ticketCode: 'TKT-0001',
      ticketId: 'TKT-0001',
      sorteoNombre: 'TICA DIARIA',
      sorteoCierre: '11:30 a. m.',
      sorteoFecha: '25/03/2026',
      fecha: '25/03/2026, 11:06 a. m.',
      vendedorNombre: '',
      localNombre: bancaNombre.trim() || '',
      local: {
        nombre: bancaNombre.trim() || '',
        descripcion: null,
        telefono: telefono.trim() || null,
        logo: bancaLogo,
        esPropio: esWeb ? esPropio : true,
        grupoId: null,
      },
      esPropio: esWeb ? esPropio : true,
      metodoPago: 'EFECTIVO',
      referencia: null,
      total: ejemplo === 'extenso' && esWeb ? 123456789 : 300,
      jugadas: [
        ...(ejemplo === 'extenso' && esWeb ? Array.from({ length: 60 }, (_, i) => ({ num1: String(i).padStart(2, '0'), monto: 1234567 })) : [{ num1: '20', monto: 300 }]),
      ],
      config,
      esReimpresion: false,
      tipoTicket: 'NUEVO',
      pagaVecesAplicado: '85',
      clienteNombre: ejemplo === 'extenso' && esWeb ? 'Cliente de ejemplo con un nombre largo para comprobar ajuste de líneas' : 'Cliente',
    };
  }, [bancaNombre, bancaLogo, notasTicketDefault, leyendaTicket, tipoImpresora, telefono, configWeb, esPropio, esWeb, ejemplo]);

  const previewHtml = useMemo(() => renderTicketPreviewHTML(previewPayload), [previewPayload]);

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl font-bold mb-6">Configuración de Ticket</h1>

      <div className="max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Formulario ── */}
          <div className="bg-white rounded-lg shadow p-6 space-y-5">
            <h2 className="text-lg font-semibold border-b pb-2">Ajustes del ticket</h2>

            {/* Marca (solo lectura) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marca <span className="text-gray-400 font-normal">(definida por BANCA)</span>
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-600 text-sm">
                {bancaNombre || 'TiemPos'}
              </div>
            </div>

            {/* Teléfono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono del local
              </label>
              <input
                type="tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="Ej: 8888-8888"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                maxLength={30}
              />
              <p className="text-xs text-gray-500 mt-1">
                Aparece en el encabezado del ticket impreso
              </p>
            </div>

            {/* Tipo de impresora */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de impresora
              </label>
              <select
                value={tipoImpresora}
                onChange={(e) => {
                  const nuevo = e.target.value === '58mm' ? '58mm' : '80mm';
                  if (esWeb && configWeb) {
                    let ajustada = configWeb;
                    for (const grupo of ['secciones', 'lineas', 'final'] as const) {
                      const preset = reconocerPresetEspacioWeb(configWeb, grupo, tipoImpresora === '58mm' ? 58 : 80);
                      if (preset) ajustada = aplicarPresetEspacioWeb(ajustada, grupo, preset, nuevo === '58mm' ? 58 : 80);
                    }
                    setConfigWeb(ajustada);
                  }
                  setTipoImpresora(nuevo);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="80mm">80 mm (estándar)</option>
                <option value="58mm">58 mm (compacta)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Define el ancho y el formato del ticket impreso
              </p>
            </div>

            {/* Leyenda final */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Leyenda final
              </label>
              <input
                type="text"
                value={leyendaTicket}
                onChange={(e) => setLeyendaTicket(e.target.value)}
                placeholder="GRACIAS POR SU PREFERENCIA"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                maxLength={200}
              />
              <p className="text-xs text-gray-500 mt-1">
                Texto que aparece al pie del ticket. Vacío = usa el texto por defecto
              </p>
            </div>

            {/* Notas internas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas internas <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <textarea
                value={notasTicketDefault}
                onChange={(e) => setNotasTicketDefault(e.target.value)}
                placeholder="Ej: Revise su ticket antes de retirarse"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={2}
                maxLength={500}
              />
            </div>

            {esWeb && <fieldset className="space-y-3 border-t pt-4">
              <legend className="font-semibold">Formato WEB</legend>
              <label className="block text-sm">Tamaño de letra
                <select aria-label="Tamaño de letra" value={configWeb?.perfil_tamano || 'actual'}
                  className="block w-full border rounded p-2"
                  onChange={e => setConfigWeb({ ...(configWeb || CONFIGURACION_WEB_ACTUAL), perfil_tamano: e.target.value as ConfiguracionTiqueteWeb['perfil_tamano'] })}>
                  <option value="actual">Formato actual</option><option value="compacto">Compacto</option>
                  <option value="normal">Normal</option><option value="grande">Grande</option>
                </select>
              </label>
              <p className="text-xs text-gray-500">Mínimo recomendado para aprovechar el papel. El formato actual se conserva hasta elegir una opción.</p>
              {([
                ['secciones', 'Espacio entre secciones'],
                ['lineas', 'Espacio entre líneas'],
                ['final', 'Espacio al final'],
              ] as const).map(([grupo, etiqueta]) => {
                const seleccionado = reconocerPresetEspacioWeb(configWeb, grupo, tipoImpresora === '58mm' ? 58 : 80);
                return <div key={grupo} role="group" aria-label={etiqueta}>
                  <p className="text-sm font-medium mb-1">{etiqueta}</p>
                  <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                    {(['minimo', 'normal', 'amplio'] as const).map((preset, indice) => <button key={preset}
                      type="button" aria-pressed={seleccionado === preset}
                      className={`px-4 py-2 text-sm ${indice ? 'border-l border-gray-300' : ''} ${seleccionado === preset ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                      onClick={() => setConfigWeb(aplicarPresetEspacioWeb(configWeb, grupo, preset, tipoImpresora === '58mm' ? 58 : 80))}>
                      {['Mínimo', 'Normal', 'Amplio'][indice]}
                    </button>)}
                  </div>
                  {!seleccionado && <p className="text-xs text-gray-500 mt-1">Se conserva la configuración guardada.</p>}
                </div>;
              })}
              <button type="button" className="border rounded px-3 py-2" onClick={() => setConfigWeb(null)}>
                Restaurar formato actual
              </button>
              <p className="text-xs text-gray-500">La restauración se guarda al pulsar Guardar configuración. Conserva ancho, teléfono y leyenda.</p>
              <label className="block text-sm">Ejemplo de vista previa
                <select aria-label="Ejemplo de vista previa" value={ejemplo} onChange={e => setEjemplo(e.target.value as 'simple' | 'extenso')} className="block border rounded p-2">
                  <option value="simple">Pocos números</option><option value="extenso">Muchos números, texto e importes largos</option>
                </select>
              </label>
            </fieldset>}

            {/* Botón guardar */}
            <button
              onClick={guardarConfig}
              disabled={loading || (esWeb && !configCargada)}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Guardando...' : 'Guardar configuración'}
            </button>

            {mensaje && (
              <div className={`p-3 rounded-md text-sm ${
                mensaje.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}>
                {mensaje}
              </div>
            )}
          </div>

          {/* ── Vista previa del ticket ── */}
          <div className={`flex flex-col ${esWeb ? 'min-w-0' : ''}`}>
            <h2 className="text-lg font-semibold border-b pb-2 mb-4">Vista previa</h2>
            <div className={`flex-1 bg-gray-100 rounded-lg p-4 overflow-auto ${esWeb ? 'min-w-0' : 'flex justify-center'}`}>
              <div className={esWeb ? 'w-max mx-auto' : undefined} style={{ position: 'relative' }}>
                <iframe
                  key={esWeb ? previewHtml : undefined}
                  title="Vista previa del ticket"
                  srcDoc={previewHtml}
                  className="bg-white shadow-md"
                  style={{
                    width: esWeb ? (tipoImpresora === '58mm' ? 'calc(48mm + 2px)' : 'calc(80mm + 2px)') : (tipoImpresora === '58mm' ? '220px' : '300px'),
                    minHeight: '520px',
                    border: '1px solid #d1d5db',
                    display: 'block',
                  }}
                  sandbox="allow-scripts"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Ancho simulado: {tipoImpresora} — los cambios se reflejan en tiempo real
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocalConfigTicketPage;
